import { EpicIcons } from "@constellation-academy/epic-ui";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { subscribeButton_SubscribeToOfferMutation } from "@relay/subscribeButton_SubscribeToOfferMutation.graphql";
import { SUBSCRIBE_TO_OFFER_MUTATION } from "./subscribe-button.graphql";
import { SubscribeButtonProps } from "./subscribe-button.types";

export const SubscribeButton = ({
	offerId,
	alreadySubscribed,
	isCompatible,
	missingElementTypes,
	onSubscribed,
}: SubscribeButtonProps) => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);
	const [confirmVisible, setConfirmVisible] = useState(false);

	const [subscribeToOffer, isSubscribing] = useMutation<subscribeButton_SubscribeToOfferMutation>(
		SUBSCRIBE_TO_OFFER_MUTATION,
	);

	const handleConfirm = () => {
		subscribeToOffer({
			variables: { offerId },
			onCompleted: (data) => {
				setConfirmVisible(false);
				const result = data.Admin.Offer.subscribeToOffer;
				if (result.kind === "OK") {
					toast.current?.show({
						severity: "success",
						life: 3000,
						summary: t("offerCatalogue.subscribeSuccess"),
						detail: t("offerCatalogue.subscribeSuccessDetail"),
					});
					onSubscribed();
				} else {
					toast.current?.show({
						severity: "error",
						life: 5000,
						summary: t("offerCatalogue.subscribeBlocked"),
						detail: t("offerCatalogue.missingElements", {
							types: (result.missingElementTypes ?? []).join(", "),
						}),
					});
				}
			},
			onError: () => {
				setConfirmVisible(false);
				toast.current?.show({
					severity: "error",
					life: 3000,
					summary: t("offerCatalogue.subscribeError"),
					detail: t("offerCatalogue.subscribeErrorDetail"),
				});
			},
		});
	};

	const getTooltip = () => {
		if (alreadySubscribed) return t("offerCatalogue.alreadySubscribed");
		if (!isCompatible)
			return t("offerCatalogue.incompatibleTooltip", {
				types: missingElementTypes.join(", "),
			});
		return undefined;
	};

	const isDisabled = alreadySubscribed || !isCompatible || isSubscribing;

	return (
		<>
			<Toast ref={toast} />
			<Button
				icon={`pi ${EpicIcons.PLUS}`}
				label={
					alreadySubscribed
						? t("offerCatalogue.subscribed")
						: t("offerCatalogue.subscribe")
				}
				disabled={isDisabled}
				tooltip={getTooltip()}
				tooltipOptions={{ position: "left" }}
				onClick={() => setConfirmVisible(true)}
				className="p-button-sm"
			/>
			<Dialog
				header={t("offerCatalogue.subscribeConfirmTitle")}
				visible={confirmVisible}
				onHide={() => setConfirmVisible(false)}
				footer={
					<div className="flex gap-2 justify-content-end">
						<Button
							label={t("actions.cancel")}
							className="p-button-secondary"
							onClick={() => setConfirmVisible(false)}
							disabled={isSubscribing}
						/>
						<Button
							label={t("offerCatalogue.subscribeConfirm")}
							loading={isSubscribing}
							onClick={handleConfirm}
						/>
					</div>
				}
			>
				<p>{t("offerCatalogue.subscribeConfirmMessage")}</p>
			</Dialog>
		</>
	);
};
